import { DragDropContext, DropResult } from '@hello-pangea/dnd';
import { BacklogColumn } from './BacklogColumn';
import { ItBacklogItem, BacklogStatus } from '@/types/itBacklog';
import { useUpdateBacklogStatus } from '@/hooks/useItBacklog';
import { format, startOfWeek, parseISO } from 'date-fns';

interface BacklogKanbanProps {
  items: ItBacklogItem[];
  currentWeek: Date;
  onItemClick: (item: ItBacklogItem) => void;
}

const COLUMNS: BacklogStatus[] = ['backlog', 'planned', 'in_progress', 'done'];

export function BacklogKanban({ items, currentWeek, onItemClick }: BacklogKanbanProps) {
  const updateStatus = useUpdateBacklogStatus();
  
  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
  const weekStartStr = format(weekStart, 'yyyy-MM-dd');
  
  // Filter items by status and week
  const getItemsForColumn = (status: BacklogStatus) => {
    if (status === 'backlog') {
      // Backlog shows items without planned_week
      return items.filter(item => item.status === 'backlog');
    }
    
    // Other columns show items for the current week
    return items.filter(item => {
      if (item.status !== status) return false;
      
      // For non-backlog, show items planned for this week
      if (item.planned_week) {
        const itemWeekDate = parseISO(item.planned_week);
        const itemWeekStart = startOfWeek(itemWeekDate, { weekStartsOn: 1 });
        return format(itemWeekStart, 'yyyy-MM-dd') === weekStartStr;
      }
      
      // Also show items without planned_week if they're in progress or done
      return status === 'in_progress' || status === 'done';
    });
  };
  
  const handleDragEnd = (result: DropResult) => {
    const { destination, source, draggableId } = result;
    
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }
    
    const newStatus = destination.droppableId as BacklogStatus;
    
    // Determine planned_week based on destination
    let plannedWeek: string | null = null;
    if (newStatus !== 'backlog') {
      plannedWeek = weekStartStr;
    }
    
    updateStatus.mutate({
      id: draggableId,
      status: newStatus,
      plannedWeek,
    });
  };
  
  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="flex gap-4 overflow-x-auto pb-4 h-full">
        {COLUMNS.map((status) => (
          <BacklogColumn
            key={status}
            status={status}
            items={getItemsForColumn(status)}
            onItemClick={onItemClick}
          />
        ))}
      </div>
    </DragDropContext>
  );
}
